import React, { useEffect, useState } from "react";
import Header from "../components/organisms/Header";
import { getRouteSegment } from "../lib/utils/funcs";
import { useUserData } from "../lib/contexts/UserContext";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import Tabs from "../components/molecules/Tabs";
import { Search } from "../assets/icons/Icons";
import Table from "../components/organisms/tables/Table";
import moment from "moment";
import CreatePrescriptionModal from "../components/organisms/modals/CreatePrescriptionModal";
import { modalStylesPrescription } from "../assets/styles/modalStyles";
import ReactDropdown from "react-dropdown";
import {
  deletePrescriptionTemplatesByTemplateId,
  getPrescriptionTemplatesByDoctorId,
} from "../lib/apis/prescription";
import { toast } from "react-toastify";
import { PrescriptionTemplate, Doctor } from "../lib/utils/types";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import SendPrescriptionModal from "../components/organisms/modals/SendPrescriptionModal";
import { getCookie } from "../lib/utils/cookies";

interface SelectedDoctor {
  name: string;
  doctorId: string;
}

const TEMPLATES_HEADERS = ["Name", "Created At", "Action"];

const CreatePrescription: React.FC = () => {
  const id = getRouteSegment(1);
  const { userData } = useUserData();
  const { hospitalID, setHospitalID, doctorsData } = useHospDocData();
  const { prescription } = useFeatureFlags();
  const timezone = getCookie("timezone") || "Asia/Kolkata";

  // State management
  const [search, setSearch] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [sendPrescriptionVisible, setSendPrescriptionVisible] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<PrescriptionTemplate[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<SelectedDoctor>({
    name: "",
    doctorId: "",
  });

  // Set hospital ID on mount
  useEffect(() => {
    setHospitalID(id);
  }, [id, setHospitalID]);

  // Set initial selected doctor
  useEffect(() => {
    if (doctorsData?.length) {
      const sortedDoctors = [...doctorsData].sort(
        (a, b) =>
          moment(a.created_at).valueOf() - moment(b.created_at).valueOf()
      );
      const firstDoctor = sortedDoctors[0];
      setSelectedDoctor({
        name: firstDoctor.full_name,
        doctorId: firstDoctor.doctor_id || "",
      });
    }
  }, [doctorsData]);

  // Fetch prescription templates
  useEffect(() => {
    const fetchPrescriptionTemplates = async () => {
      if (!selectedDoctor.doctorId) return;

      try {
        const data = await getPrescriptionTemplatesByDoctorId(
          selectedDoctor.doctorId
        );
        if (data?.status === 200 && Array.isArray(data.data.result)) {
          setTemplates(data.data.result);
        } else {
          console.warn("Invalid API response format:", data);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("Failed to load prescription templates");
      }
    };

    fetchPrescriptionTemplates();
  }, [selectedDoctor.doctorId, modalIsOpen]);

  // Filter templates based on search
  const filteredTemplates = templates.filter((template) => {
    const searchLower = search.toLowerCase();
    return (
      template.template_name.toLowerCase().includes(searchLower) ||
      moment(template.created_at)
        .format("DD MMM, YYYY")
        .toLowerCase()
        .includes(searchLower)
    );
  });

  // Reset template ID when creating new template
  useEffect(() => {
    if (modalType === "create") {
      setTemplateId(null);
    }
  }, [modalIsOpen, modalType]);

  // Modal handlers
  const openCreateTemplateModal = () => {
    setModalType("create");
    setModalIsOpen(true);
  };

  const closeCreateTemplateModal = () => {
    setModalIsOpen(false);
  };

  const handleDisableButton = () => {
    if (!prescription) {
      toast.info(
        "Please subscribe for prescription model to access this feature"
      );
    }
  };

  const handleDeleteTemplate = async (template_id: string) => {
    // Show confirmation dialog before deletion
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this template?"
    );

    if (isConfirmed) {
      const res = await deletePrescriptionTemplatesByTemplateId(template_id);
      if (res.status === 200 || res.status === 201) {
        toast.success("Template deleted successfully");
        // Safely invoke setTemplates if it's defined
        setTemplates?.((prevTemplates: PrescriptionTemplate[]) =>
          prevTemplates.filter(
            (template: PrescriptionTemplate) =>
              template.template_id !== template_id
          )
        );
      } else {
        toast.error(res.error);
      }
    } else {
      toast.info("Template deletion cancelled");
    }
  };
  // Doctor dropdown options
  const doctorOptions =
    doctorsData
      ?.filter(
        (doctor): doctor is Doctor & { doctor_id: string } =>
          typeof doctor.doctor_id === "string"
      )
      .map((doctor) => ({
        value: doctor.doctor_id,
        label: doctor.full_name,
      })) || [];

  const tabsConfig = [
    {
      label: "Templates",
      content: !filteredTemplates?.length ? (
        <div className="py-10 flex flex-col justify-center items-center">
          <img
            src="https://static.vecteezy.com/system/resources/previews/012/181/008/non_2x/document-data-file-not-found-concept-illustration-flat-design-eps10-modern-graphic-element-for-landing-page-empty-state-ui-infographic-icon-etc-vector.jpg"
            alt="Queue Empty"
            className="w-1/4 h-auto"
          />
          <p className="text-sbTextHover pt-4 font-medium">
            {search
              ? "No templates found matching your search"
              : "No templates found"}
          </p>
        </div>
      ) : (
        <div className="md:min-h-auto min-h-[45vh] md:min-h-auto max-h-[45vh] md:max-h-[70vh] lg:max-h-[55vh] overflow-y-auto">
          <Table
            type="create_prescription"
            headers={TEMPLATES_HEADERS}
            templates={filteredTemplates}
            setModalType={setModalType}
            setModalIsOpen={setModalIsOpen}
            setTemplateId={setTemplateId}
            handleDeleteTemplate={handleDeleteTemplate}
            setSendPrescriptionVisible={setSendPrescriptionVisible}
          />
        </div>
      ),
    },
  ];

  const canCreateTemplate =
    userData?.role === "MasterAdmin" || userData?.role === "Doctor";
  const selectedDoctorMappingId =
    doctorsData?.find((e) => e.doctor_id === selectedDoctor.doctorId)
      ?.mapping_id || "";

  return (
    <div className="flex flex-col w-full h-[100vh] overflow-y-auto side_bar">
      <Header header value="Create Prescriptions" />
      <div className="flex flex-col bg-white rounded-t-lg mt-6 ml-7 mr-8 pt-4 border-[0.5px] border-doctorsBorder">
        <Tabs
          tabsConfig={tabsConfig}
          rightSide={
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-end text-sm !m-[10px] md:m-0 w-[150%]">
              {userData?.role !== "MasterAdmin" && (
                <button
                  className="flex w-full lg:w-[150px] text-white text-center justify-center px-4 py-2 rounded-lg bg-sbTextHover hover:bg-queueHover opacity-80"
                  onClick={() => setSendPrescriptionVisible(true)}
                >
                  Send Prescription
                </button>
              )}
              {canCreateTemplate && prescription ? (
                <button
                  className="flex w-full lg:w-[150px] text-white text-center justify-center px-4 py-2 rounded-lg bg-sbTextHover hover:bg-queueHover opacity-80"
                  onClick={openCreateTemplateModal}
                >
                  Create <span className="ml-1">Template</span>
                </button>
              ) : (
                <button
                  className="flex w-full lg:w-[150px] text-white text-center justify-center px-4 py-2 rounded-lg bg-sbTextHover hover:bg-queueHover opacity-80 cursor-not-allowed"
                  onClick={handleDisableButton}
                >
                  Create Template
                </button>
              )}

              <div className="flex items-center w-full lg:w-[400px] xl:w-[400px] rounded-lg border-[0.5px] border-sbBorder bg-white justify-between">
                <input
                  className="border-0 p-0 px-4 py-2 border-sbBorder opacity-80 rounded-lg focus:outline-none w-max"
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <p className="w-[20px] relative md:right-2">
                  <Search />
                </p>
              </div>

              <ReactDropdown
                options={doctorOptions}
                onChange={(option) =>
                  setSelectedDoctor({
                    name: String(option.label),
                    doctorId: option.value,
                  })
                }
                value={
                  doctorsData?.length === 0
                    ? "No doctors found"
                    : selectedDoctor.name.length > 15
                    ? `${selectedDoctor.name.substring(0, 15)}...`
                    : selectedDoctor.name
                }
                placeholder="Select a doctor"
                controlClassName="!rounded-lg w-full lg:w-[180px] !cursor-pointer"
                menuClassName="overflow-x-hidden side_bar rounded-lg !mt-1"
              />
            </div>
          }
        />
      </div>

      <CreatePrescriptionModal
        closeModal={closeCreateTemplateModal}
        modalIsOpen={modalIsOpen}
        customStyles={modalStylesPrescription}
        type={modalType || ""}
        templateId={templateId}
        doctorId={selectedDoctor.doctorId}
        mappingId={selectedDoctorMappingId}
      />

      <SendPrescriptionModal
        closeModal={() => setSendPrescriptionVisible(false)}
        modalIsOpen={sendPrescriptionVisible}
        customStyles={modalStylesPrescription}
        bookingID={null}
        patientID={null}
        patientName={null}
        mappingId={selectedDoctorMappingId}
        templateId={templateId}
        bookedDate={moment().tz(timezone).format("YYYY-MM-DD")}
        allBookings={[]}
        type="direct"
      />
    </div>
  );
};

export default CreatePrescription;
